import React, { useState, useEffect, useRef, useCallback } from 'react';
import CandidateSidebar from '../components/candidate/CandidateSidebar';
import CandidateMobileHeader from '../components/candidate/CandidateMobileHeader';
import CandidateMobileMenu from '../components/candidate/CandidateMobileMenu';
import { Bookmark, Trash2, ExternalLink, MapPin, Briefcase, DollarSign } from 'lucide-react';
import useResponsive from '../hooks/useResponsive';
import api from '../api/api';
import { Link } from 'react-router-dom';

const SavedJobsPage = () => {
  const { isMobile } = useResponsive();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const [savedJobs, setSavedJobs] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Fetch saved jobs ONCE on mount
  useEffect(() => {
    const fetchSavedJobs = async () => {
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        setLoading(true);
        try {
          const response = await api.get('/saved-jobs');
          setApiData(response.data);
          setError(null);
        }
        catch (err) {
          console.error('Failed to fetch saved jobs:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchSavedJobs();
  }, []);
  
  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/saved-jobs');
      setApiData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Update saved jobs when API data is received
  useEffect(() => {
    if (apiData && apiData.savedJobs) {

      setSavedJobs(apiData.savedJobs);
    } else if (!loading) {

      setSavedJobs([]);
    }
  }, [apiData, loading]);

  const [unsavingJob, setUnsavingJob] = useState(null);

  const handleUnsaveJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to remove this job from your saved list?')) {
      return;
    }
    
    try {
      setUnsavingJob(jobId);
      await api.delete(`/saved-jobs?id=${jobId}`);

      await refetch();
    } catch (err) {
      console.error('Failed to unsave job:', err);
      alert('Failed to remove job from saved list. Please try again.');
    } finally {
      setUnsavingJob(null);
    }
  };

  return (
    <div className="flex flex-col md:flex-row bg-white min-h-screen font-sans relative">
      {isMobile && <CandidateMobileHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />}
      {isMobile && mobileMenuOpen && <CandidateMobileMenu setMobileMenuOpen={setMobileMenuOpen} />}
      <CandidateSidebar />
      
      <main className="flex-1 p-4 md:p-8 ml-0 md:ml-24">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black mb-2">Saved Jobs</h1>
          <p className="text-gray-600">Your bookmarked job opportunities</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading saved jobs...</div>
          </div>
        ) : savedJobs.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Bookmark size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No saved jobs yet</h3>
            <p className="text-gray-600 mb-6">Start bookmarking jobs you're interested in</p>
            <Link 
              to="/jobs"
              className="inline-block bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Browse Jobs
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {savedJobs.map((job) => (
              <div 
                key={job.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <Link 
                      to={`/candidate/jobs/${job.id}`}
                      className="text-xl font-semibold text-black hover:text-gray-700 mb-2 inline-flex items-center"
                    >
                      {job.title}
                    </Link>
                    <p className="text-gray-600">{job.company}</p>
                  </div>
                  <button
                    onClick={() => handleUnsaveJob(job.id)}
                    disabled={unsavingJob === job.id}
                    className="text-red-500 hover:text-red-700 p-2 disabled:opacity-50"
                    title="Remove from saved jobs"
                  >
                    {unsavingJob === job.id ? (
                      <span className="inline-block animate-spin">⌛</span>
                    ) : (
                      <Trash2 size={18} />
                    )}
                  </button>
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    {job.location}
                  </div>
                  <div className="flex items-center">
                    <Briefcase size={16} className="mr-1" />
                    {job.type}
                  </div>
                  {job.salary && (
                    <div className="flex items-center">
                      <DollarSign size={16} className="mr-1" />
                      {job.salary}
                    </div>
                  )}
                </div>

                {job.skills && job.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {job.skills.slice(0, 5).map((skill, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                    {job.skills.length > 5 && (
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                      </span>
                    )}
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <span className="text-sm text-gray-500">
                    Saved {new Date(job.savedAt).toLocaleDateString()}
                  </span>
                  <Link
                    to={`/candidate/jobs/${job.id}`}
                    className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SavedJobsPage;
